/* Shopify renders option/price/form state. No variant dataset is copied into JS. */
if (!customElements.get('product-detail')) {
  customElements.define('product-detail', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      this.addEventListener('click', event => this.selectOption(event), { signal: this.lifecycle.signal });
      this.desktop = matchMedia('(min-width: 750px)');
      const disclosures = [...this.querySelectorAll('.pdp-disclosure')];
      const configureDisclosures = () => {
        this.toggleAttribute('data-wide-disclosures', this.desktop.matches);
        disclosures.forEach(item => item.removeAttribute('name'));
        if (this.desktop.matches) {
          const firstOpen = disclosures.find(item => item.open) || disclosures[0];
          disclosures.forEach(item => {
            item.open = item === firstOpen;
            item.setAttribute('name', 'product-information-' + this.dataset.sectionId);
          });
        }
      };
      this.desktop.addEventListener('change', configureDisclosures, { signal: this.lifecycle.signal });
      disclosures.forEach(item => item.querySelector('summary')?.addEventListener('click', event => {
        // Keyboard activation also dispatches click; retain native summary semantics.
        if (this.desktop.matches && item.open) event.preventDefault();
      }, { signal: this.lifecycle.signal }));
      disclosures.forEach(item => item.addEventListener('toggle', () => {
        if (this.desktop.matches && item.open) disclosures.forEach(other => { if (other !== item) other.open = false; });
      }, { signal: this.lifecycle.signal }));
      configureDisclosures();
      this.addEventListener('submit', event => this.addToCart(event), { signal: this.lifecycle.signal });
    }

    async addToCart(event) {
      if (this.pending) { event.preventDefault(); return; }
      const form = event.target.closest('.pdp-form');
      if (!form) return;
      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      const status = this.querySelector('[data-variant-status]');
      const wasDisabled = submit.disabled;
      this.pending = true;
      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      try {
        const endpoint = form.action.endsWith('.js') ? form.action : `${form.action}.js`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.addError);
        const overlay = document.querySelector('cart-overlay');
        if (!overlay) { location.assign('/cart'); return; }
        await overlay.open(submit);
      } catch (error) {
        status.textContent = error.message;
        status.classList.remove('landing-visually-hidden');
      } finally {
        this.pending = false;
        submit.disabled = wasDisabled;
        form.removeAttribute('aria-busy');
      }
    }

    async selectOption(event) {
      const link = event.target.closest('[data-product-option]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href);
      // Combined-listing links navigate normally to the sibling product.
      if (destination.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) return;
      event.preventDefault();
      this.request?.abort();
      const request = this.request = new AbortController();
      this.pending = true;
      const purchase = this.querySelector('[data-product-purchase]');
      purchase.setAttribute('aria-busy', 'true');
      purchase.querySelector('[type="submit"]').disabled = true;
      const requestUrl = new URL(destination);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      // Preserve preview/localization parameters without carrying an old variant.
      const currentUrl = new URL(location.href);
      for (const key of ['preview_theme_id', 'view']) {
        if (currentUrl.searchParams.has(key)) requestUrl.searchParams.set(key, currentUrl.searchParams.get(key));
      }
      try {
        const response = await fetch(requestUrl, { signal: request.signal });
        if (!response.ok) throw new Error('Product section unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('[data-product-purchase]');
        if (!replacement) throw new Error('Product section missing');
        if (request.signal.aborted || !this.isConnected) return;
        const restoreFocus = purchase.contains(document.activeElement);
        purchase.replaceChildren(...replacement.childNodes);
        const option = [...purchase.querySelectorAll('[data-option-id]')].find(item => item.dataset.optionId === link.dataset.optionId);
        if (restoreFocus) option?.focus({ preventScroll: true });
        this.querySelector('[data-variant-status]').textContent = [
          ...[...purchase.querySelectorAll('[aria-current]')].map(item => item.textContent.trim()),
          purchase.querySelector('[data-product-price]').textContent.trim(),
          purchase.querySelector('[type="submit"]').textContent.trim(),
        ].join(', ');
        currentUrl.searchParams.delete('variant');
        currentUrl.searchParams.set('option_values', destination.searchParams.get('option_values'));
        history.replaceState({}, '', currentUrl);
      } catch (error) {
        if (error.name !== 'AbortError') location.assign(destination.href);
      } finally {
        if (this.request === request) {
          this.pending = false;
          purchase.removeAttribute('aria-busy');
        }
      }
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
    }
  });
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll('[data-gallery-index]')];
      this.active = -1;
      this.querySelectorAll('[data-gallery-step], [data-gallery-index]').forEach(button => { button.hidden = false; });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (button?.hasAttribute('data-gallery-index')) this.goTo(Number(button.dataset.galleryIndex));
        if (button?.hasAttribute('data-gallery-step')) this.goTo(this.active + Number(button.dataset.galleryStep));
      }, { signal: this.lifecycle.signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const step = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0;
        if (step) { event.preventDefault(); this.goTo(this.active + step); }
      }, { signal: this.lifecycle.signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal: this.lifecycle.signal });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.update();
    }

    goTo(index, behavior) {
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const first = this.slides[0].getBoundingClientRect();
      const target = this.slides[index].getBoundingClientRect();
      const distance = rtl ? first.right - target.right : target.left - first.left;
      this.track.scrollTo({ left: distance * (rtl ? -1 : 1), behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') });
    }

    update() {
      const bounds = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.classList.toggle('pdp-gallery--overflow', overflow);
      const distances = this.slides.map(slide => {
        const box = slide.getBoundingClientRect();
        return Math.abs(rtl ? box.right - bounds.right : box.left - bounds.left);
      });
      const index = distances.indexOf(Math.min(...distances));
      const changed = index !== this.active;
      this.active = index;
      this.slides.forEach(slide => {
        // Keep off-screen video/model controls out of keyboard navigation.
        const box = slide.getBoundingClientRect();
        const visible = Math.min(box.right, bounds.right) - Math.max(box.left, bounds.left) >= box.width / 2;
        const becameHidden = !visible && !slide.inert;
        if (!visible && slide.contains(document.activeElement)) this.track.focus({ preventScroll: true });
        slide.inert = !visible;
        slide.setAttribute('aria-hidden', String(!visible));
        if (becameHidden) {
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
          slide.querySelectorAll('iframe').forEach(frame => {
            const source = new URL(frame.src);
            if (source.hostname === 'www.youtube.com' || source.hostname === 'www.youtube-nocookie.com') frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), source.origin);
            if (source.hostname === 'player.vimeo.com') frame.contentWindow?.postMessage({ method: 'pause' }, source.origin);
          });
        }
      });
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const previous = this.querySelector('[data-gallery-step="-1"]');
      const next = this.querySelector('[data-gallery-step="1"]');
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2;
      if (changed) this.querySelector('[data-gallery-status]').textContent = this.slides[index].getAttribute('aria-label');
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}

if (!customElements.get('product-complementary')) {
  customElements.define('product-complementary', class extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[data-complementary-content]')) return;
      this.request?.abort();
      this.request = new AbortController();
      this.load(this.request.signal);
    }

    async load(signal) {
      try {
        const response = await fetch(this.dataset.url, { signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-complementary-content]');
        if (!content || signal.aborted || !this.isConnected) return;
        this.replaceChildren(content);
        this.hidden = false;
      } catch (error) {
        // Recommendations are optional; failed/empty responses stay hidden.
      }
    }

    disconnectedCallback() { this.request?.abort(); }
  });
}
